import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';

import { OpSenseToastContainer, toast } from "../Toast";

import { PageTitle } from '../PageTitle';
import { LocalesService, UsersService } from "../../services";
import UserUpdatePasswordForm from "./user_update_password_form";
import UserUpdateLocaleForm from "./user_update_locale_form";
import UserUpdateTempUnitForm from "./user_update_temp_unit_form";
import UserEnable2FAForm from './user_enable_2fa_form';
import i18next from "i18next";
import QRCode from 'react-qr-code';

const TEMPERATURE_DISPLAY_UNITS = [
  { value: "C", label: "Celsius" },
  { value: "F", label: "Fahrenheit" }
];

const UserProfile = ({ me }) => {
  const [twoFactorQRCode, setTwoFactorQRCode] = useState(null);
  const [show2FAModal, setShow2FAModal] = useState(null);
  const [localeOptions, setLocaleOptions] = useState(null);
  const [selectedLocale, setSelectedLocale] = useState(null);
  const temperatureDisplayUnitOptions = TEMPERATURE_DISPLAY_UNITS.find((unit) => unit.value == me.temperatureDisplayUnit);

  useEffect(() => {
    LocalesService.getAll().then(response => {
      const selectedLocale = response.locales.find((locale) => locale.languageTag === me.locale.languageTag);
      setSelectedLocale(selectedLocale ? {value: selectedLocale.languageTag, label: selectedLocale.description} : {value: response.locales[0].languageTag, label: response.locales[0].description});
      setLocaleOptions(response.locales.map(locale => {return {value: locale.languageTag, label: locale.description}}));
    }).catch(err => {
      console.error(err);
      toast.error(i18next.t('messages.could-not-load-languages'));
    });
  }, [me]);

  const handlePasswordSubmit = useCallback(({ password }) => {
    UsersService.saveMe({ ...me, locale: me.locale.languageTag, password: password, availableRoutes: null, availableGroupings: null }).then(() => {
      toast.success(i18next.t('messages.password-updated-successfully'));
    }).catch(error => {
      console.error(error);
      toast.error(error.message);
    });
  }, [me]);

  const handleLocaleSubmit = useCallback(({ locale }) => {
    UsersService.saveMe({ ...me, locale: locale.value, availableRoutes: null, availableGroupings: null }).then(() => {
      toast.success(i18next.t("messages.language-updated-successfully"));
      i18next.changeLanguage(locale.value);
    }).catch(error => {
      console.error(error);
      toast.error(error.message);
    });
  }, [me]);

  const handleTempUnitSubmit = useCallback(({ temperatureDisplayUnit }) => {
    UsersService.saveMe({ ...me, locale: me.locale.languageTag, temperatureDisplayUnit: temperatureDisplayUnit.value, availableRoutes: null, availableGroupings: null }).then(() => {
      toast.success("Display unit updated");
    }).catch(error => {
      console.error(error);
      toast.error(error.message);
    });
  }, [me]);

  const handleEnable2FASubmit = useCallback(({ enable2fa }) => {
    UsersService.saveMe2FA(enable2fa === true).then(({data}) => {
      if (enable2fa) {
        setShow2FAModal(true);
        setTwoFactorQRCode(`otpauth://totp/(OpSense) ${data.username}?secret=${data.twoFactorValue}&issuer=OpSense&algorithm=SHA1&digits=${data.digits}&period=${data.period}`);
      } else {
        toast.success("2FA disabled.");
      }
    }).catch(error => {
      console.error(error);
      toast.error(error.message);
    });
  }, [me]);

  if (me && localeOptions && selectedLocale) {
    return (
        <div>
          <div className="row">
            <PageTitle title={`${me.firstName} ${me.lastName}`}
                       customButton={{
                          link: { pathname: "/user/profile/notifications" },
                          title: i18next.t('pages.users.notifications-info') 
                       }} 
            />
          </div>                        
          
          <OpSenseToastContainer />

          <div className="row">
            <div className="content-wrapper__centered-content margin-bottom-sm">              
              <div className="container-child__50">
                <div className="op-content-box">
                  <div className="list-group remove-margin-bottom">
                    <div className="list-group-item">
                      <div>
                        <span className="list-title">{i18next.t('pages.users.your-name')}</span>
                      </div>
                      <div>{me.firstName} {me.lastName}</div>
                    </div>
                    <div className="list-group-item">
                      <div>
                        <span className="list-title">{i18next.t('pages.users.login-username')}</span>
                      </div>
                      <div>{me.username}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="container-child__50">
                <UserUpdateLocaleForm locales={localeOptions} selectedLocale={selectedLocale} onSubmit={handleLocaleSubmit} />
              </div>

              <div className="container-child__50">
                <UserUpdateTempUnitForm units={TEMPERATURE_DISPLAY_UNITS} selectedUnit={temperatureDisplayUnitOptions} onSubmit={handleTempUnitSubmit} />              
              </div>

              <div className="container-child__50">
                <UserUpdatePasswordForm user={me} onSubmit={handlePasswordSubmit} errorMessage={(errorMessage) => console.log(errorMessage)} />
              </div>

              <div className="container-child__50">
                <UserEnable2FAForm requireTwoFactor={me.requireTwoFactor} onSubmit={handleEnable2FASubmit} errorMessage={(errorMessage) => console.log(errorMessage)} />
              </div>
            </div>
          </div>

          <Modal show={show2FAModal} onHide={() => setShow2FAModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{i18next.t('two-factor-auth.modal-title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <p>{i18next.t('two-factor-auth.scan-instruction')}</p>
                <div className='text-center'>
                  {twoFactorQRCode && <QRCode value={twoFactorQRCode} size={200} />}
                  <p className='footnote-danger'>{i18next.t('two-factor-auth.scan-warning')}</p>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setShow2FAModal(false)}>{i18next.t('form-labels.close')}</Button>
            </Modal.Footer>
        </Modal>
        </div>
    );
  } else {
    return (<div className="loader">{i18next.t('loading')}</div>)
  }
}

export default connect(({ user }) => ({ me: user.me } ))(UserProfile);
